import React, { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import styles from './GravityForm.module.scss';
import { RiCheckLine } from 'react-icons/ri';

// Dynamically import ReCaptcha to avoid SSR issues
const ReCAPTCHA = dynamic(() => import('react-google-recaptcha'), {
  ssr: false,
  loading: () => <div>Loading reCAPTCHA...</div>
});

export default function GravityForm({ formId = 3 }) {
  const router = useRouter();
  const [formConfig, setFormConfig] = useState(null);

  useEffect(() => {
    let isMounted = true;
    async function loadConfig() {
      let config;
      if (formId === 4) {
        config = await import('../../../public/ACF/stilling-form.json');
      } else {
        config = await import('../../../public/ACF/contact-form.json');
      }
      if (isMounted) setFormConfig(config.default || config);
    }
    loadConfig();
    return () => { isMounted = false; };
  }, [formId]);

  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    services: [],
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  
  if (!formConfig) return <div>Laster skjema…</div>;

  // Get form fields from the configuration
  const formFields = formConfig["0"].fields;
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'consent') {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      } else if (name === 'services') {
        setFormData(prev => {
          const updatedServices = checked
            ? [...prev.services, value]
            : prev.services.filter(service => service !== value);
          
          return {
            ...prev,
            services: updatedServices
          };
        });
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If not on the last page, move to next page
    if (currentPage < 4) {
      setCurrentPage(prev => prev + 1);
      return;
    }

    // Validate all required fields
    if (!formData.fullName) {
      setError("Vennligst fyll inn fullt navn");
      return;
    }
    
    if (!formData.company) {
      setError("Vennligst fyll inn bedrift/organisasjon");
      return;
    }
    
    if (!formData.phone) {
      setError("Vennligst fyll inn telefonnummer");
      return;
    }
    
    if (formData.services.length === 0) {
      setError("Vennligst velg minst én tjeneste");
      return;
    }
    
    if (!formData.consent) {
      setError("Du må godta personvernerklæringen");
      return;
    }
    
    if (!recaptchaToken) {
      setError("Vennligst bekreft at du ikke er en robot");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Map form data to the exact structure expected by Gravity Forms
      const inputValues = {};
      
      // Find the checkbox field (id: 29) and map services
      const checkboxField = formFields.find(field => field.id === 29);
      if (checkboxField && checkboxField.inputs) {
        checkboxField.inputs.forEach((input, index) => {
          const choiceValue = checkboxField.choices[index]?.value;
          if (choiceValue && formData.services.includes(choiceValue)) {
            inputValues[`input_${input.id}`] = choiceValue;
          }
        });
      }
      
      // Map other fields
      inputValues['input_4'] = formData.fullName; // Full name
      inputValues['input_15'] = formData.company; // Company
      inputValues['input_17'] = formData.email; // Email
      inputValues['input_24'] = formData.phone; // Phone
      inputValues['input_21'] = formData.message; // Message
      
      // Hidden field for source tracking (field 31)
      inputValues['input_31'] = 'Kontaktskjema - kontakt';
      
      // Add reCAPTCHA token
      inputValues['g-recaptcha-response'] = recaptchaToken;
      
      // Consent field
      const consentField = formFields.find(field => field.id === 23);
      if (consentField && consentField.inputs) {
        inputValues['input_23.1'] = formData.consent ? '1' : '';
      }
      
      const formBody = {
        input_values: inputValues
      };

      console.log('Submitting form data:', formBody);

      const response = await fetch(`${process.env.NEXT_PUBLIC_WORDPRESS_URL}/wp-json/gf/v2/forms/${formId}/submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formBody)
      });

      console.log('Response status:', response.status);
      const responseData = await response.json();
      console.log('Response data:', responseData);

      // Check if the form was successfully submitted
      if (response.status === 201 && responseData.is_valid === true) {
        const thankYouUrl = `${process.env.NEXT_PUBLIC_WORDPRESS_URL}/takkeside/`;
        try {
          const parsedUrl = new URL(thankYouUrl, window.location.origin);
          if (parsedUrl.origin === window.location.origin) {
            router.push(`${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`);
          } else {
            window.location.assign(thankYouUrl);
          }
        } catch (redirectError) {
          window.location.assign(thankYouUrl);
        }
      } else {
        // Form submission failed
        let errorMessage = 'Det oppstod en feil ved sending av skjemaet.';
        
        // Check if there are validation errors
        if (responseData.validation_messages) {
          errorMessage = Object.values(responseData.validation_messages).join(', ');
        }
        
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setError('Det oppstod en feil ved sending av skjemaet. Vennligst prøv igjen senere.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    if (error && error.includes('robot')) {
      setError(null);
    }
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
  };

  const validateCurrentPage = () => {
    switch (currentPage) {
      case 1:
        // First page requires services selection and consent
        return formData.services.length > 0 && formData.consent;
      case 2:
        // Second page requires name and company
        return formData.fullName && formData.company;
      case 3:
        // Third page requires phone (email is optional)
        return formData.phone;
      case 4:
        // Fourth page has optional message, no validation needed
        return true;
      default:
        return true;
    }
  };

  const renderPageContent = () => {
    // Get the checkbox field for services
    const servicesField = formFields.find(field => field.id === 29);
    const servicesChoices = servicesField?.choices || [];
    
    switch (currentPage) {
      case 1:
        return (
          <div className={styles.formSection}>
            <h3 className={styles.formHeading}>1. Hva trenger du hjelp med?</h3>
            <div className={styles.checkboxGroup}>
              {servicesChoices.map((choice, index) => (
                <label key={index} className={styles.checkbox}>
                  <input
                    type="checkbox"
                    name="services"
                    value={choice.value}
                    checked={formData.services.includes(choice.value)}
                    onChange={handleChange}
                  />
                  {choice.text}
                </label>
              ))}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.consentCheckbox}>
                <input
                  type="checkbox"
                  name="consent"
                  required
                  checked={formData.consent}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                Jeg godtar personvernerklæringen.
              </label>
            </div>
          </div>
        );
      case 2:
        return (
          <div className={styles.formSection}>
            <h3 className={styles.formHeading}>2. Vi trenger litt info!</h3>
            <div className={styles.formGroup}>
              <label htmlFor="fullName" className={styles.label}>
                Fullt navn
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                required
                placeholder="Ditt fulle navn"
                className={styles.input}
                value={formData.fullName}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="company" className={styles.label}>
                Hvilken bedrift/organisasjon gjelder det?
              </label>
              <input
                type="text"
                id="company"
                name="company"
                required
                placeholder="Navn på bedrift/organisasjon"
                className={styles.input}
                value={formData.company}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div className={styles.formSection}>
            <h3 className={styles.formHeading}>3. Trenger litt mer!</h3>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  E-post
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Din e-postadresse"
                  className={styles.input}
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="phone" className={styles.label}>
                  Telefon
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  placeholder="Ditt telefonnummer"
                  className={styles.input}
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <>
            <div className={styles.formSection}>
              <h3 className={styles.formHeading}>4. Dette er siste, lover!</h3>
              <div className={styles.formGroup}>
                <label htmlFor="message" className={styles.label}>
                  Noe mer du vil tilføye?
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  placeholder="Vennligst legg inn en beskrivelse for din henvendelse ..."
                  className={styles.textarea}
                  value={formData.message}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>
              
              {/* reCAPTCHA */}
              <div className={`${styles.formGroup} ${styles.recaptchaContainer}`}>
                {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ? (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                    onChange={handleRecaptchaChange}
                    onExpired={handleRecaptchaExpired}
                    theme="light"
                    size="normal"
                  />
                ) : (
                  <div style={{ padding: '10px', background: '#f0f0f0', border: '1px solid #ccc' }}>
                    DEBUG: reCAPTCHA site key not found. Expected: NEXT_PUBLIC_RECAPTCHA_SITE_KEY
                  </div>
                )}
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.formContainer}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.progressBar}>
          <div 
            className={styles.progressBarFill} 
            style={{ width: `${((currentPage - 1) / 3) * 100}%` }}
          ></div>
          <div className={styles.progressSteps}>
            {[1, 2, 3, 4].map(step => (
              <div 
                key={step}
                className={`${styles.progressStep} ${
                  step < currentPage ? styles.completed : 
                  step === currentPage ? styles.active : ''
                }`}
              >
                {step < currentPage ? <RiCheckLine size={14} /> : step}
              </div>
            ))}
          </div>
        </div>
        
        {renderPageContent()}
        
        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}
        
        <div className={styles.formNavigation}>
          {currentPage > 1 && (
            <button 
              type="button" 
              className={styles.navigationButton}
              onClick={handlePrevPage}
              disabled={isSubmitting}
            >
              Forrige
            </button>
          )}
          
          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={isSubmitting || !validateCurrentPage()}
          >
            {isSubmitting ? (
              <>
                <span className={styles.spinner}></span>
                Sender...
              </>
            ) : currentPage < 4 ? 'Neste' : 'Send inn'}
          </button>
        </div>
      </form>
    </div>
  );
} 